import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Save, Loader2, Briefcase, Users } from "lucide-react";

const emptyJob = () => ({
  title: "", location: "", level: "", department: "",
  type: "Full-time", summary: "", description: "",
  apply_email: "", position: 0, active: true,
});

function JobsList({ jobs, onEdit, onDelete, onAdd, loading, saving }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Open Positions</h3>
          <p className="text-xs text-slate-500 mt-0.5">These job cards appear on the marketing site&apos;s Careers page.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onAdd} data-testid="job-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1"/> Add Job
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>}

      {!loading && jobs.length === 0 && (
        <div className="border border-dashed rounded p-8 text-center text-sm text-slate-500">
          No open positions yet. Click <span className="font-semibold">Add Job</span> to create the first one.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {jobs.map((j, idx) => (
          <div key={j.id || idx} className="border rounded-lg p-4 bg-white hover:shadow-sm transition" data-testid={`job-card-${idx}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{j.title || "(untitled)"}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{[j.department, j.level, j.location].filter(Boolean).join(" · ")}</div>
              </div>
              <div className={"px-2 py-0.5 rounded-full text-[10px] font-medium " + (j.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600")}>
                {j.active ? "Live" : "Draft"}
              </div>
            </div>
            {j.summary && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{j.summary}</p>}
            <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => onEdit(j)} data-testid={`job-edit-${idx}`}>Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(j)} data-testid={`job-delete-${idx}`} disabled={saving}>
                <Trash2 className="w-3.5 h-3.5 text-red-600"/>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function JobEditor({ job, onCancel, onSave, saving }) {
  const [form, setForm] = useState(job);
  useEffect(() => { setForm(job); }, [job]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="border rounded-lg p-6 bg-slate-50" data-testid="job-editor">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-slate-900">{form.id ? "Edit Job" : "New Job"}</h4>
        <div className="flex items-center gap-2 text-xs">
          Live
          <Switch checked={!!form.active} onCheckedChange={(v) => set("active", v)} data-testid="job-active-switch"/>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Job Title</Label>
          <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Senior Backend Engineer" data-testid="job-title"/>
        </div>
        <div><Label>Department</Label><Input value={form.department || ""} onChange={(e) => set("department", e.target.value)} placeholder="Engineering" data-testid="job-dept"/></div>
        <div><Label>Level</Label><Input value={form.level || ""} onChange={(e) => set("level", e.target.value)} placeholder="Senior" data-testid="job-level"/></div>
        <div><Label>Location</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Pune, India / Remote" data-testid="job-location"/></div>
        <div><Label>Type</Label><Input value={form.type || ""} onChange={(e) => set("type", e.target.value)} placeholder="Full-time" data-testid="job-type"/></div>
        <div className="md:col-span-2">
          <Label>Short Summary</Label>
          <Input value={form.summary || ""} onChange={(e) => set("summary", e.target.value)} placeholder="One-liner shown on the Careers grid." data-testid="job-summary"/>
        </div>
        <div className="md:col-span-2">
          <Label>Full Description (Markdown supported)</Label>
          <Textarea rows={8} value={form.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="Responsibilities, requirements, benefits…" data-testid="job-desc"/>
        </div>
        <div className="md:col-span-2">
          <Label>Notification email (override)</Label>
          <Input value={form.apply_email || ""} onChange={(e) => set("apply_email", e.target.value)} placeholder="Leave blank to use default recipient" data-testid="job-apply-email"/>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={() => onSave(form)} disabled={saving || !form.title} data-testid="job-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1"/>}
          Save
        </Button>
      </div>
    </div>
  );
}

function Applications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sbt-site/careers/applications");
      setRows(r.data.applications || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load applications");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/sbt-site/careers/applications/${id}/status?status=${status}`);
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, status } : r));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (loading) return <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading applications…</div>;

  if (!rows.length) return (
    <div className="border border-dashed rounded p-8 text-center text-sm text-slate-500">
      No applications yet — they'll appear here once candidates apply via the marketing site.
    </div>
  );

  return (
    <div className="overflow-x-auto border rounded" data-testid="applications-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left">
          <tr>
            <th className="px-3 py-2">Candidate</th>
            <th className="px-3 py-2">Job</th>
            <th className="px-3 py-2">Resume</th>
            <th className="px-3 py-2">Applied</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
              </td>
              <td className="px-3 py-2">{r.job_title || r.job_id}</td>
              <td className="px-3 py-2">
                {r.resume?.upload_id ? (
                  <a href={`/api/sbt-site/uploads/${r.resume.upload_id}`} target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline text-xs">{r.resume.original_filename || "Download"}</a>
                ) : <span className="text-xs text-slate-400">—</span>}
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">{r.created_at?.slice(0, 10)}</td>
              <td className="px-3 py-2">
                <select value={r.status || "new"} onChange={(e) => setStatus(r.id, e.target.value)} className="text-xs border rounded px-2 py-1 bg-white">
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                  <option value="hired">Hired</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminSbtCareers() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sbt-site/careers");
      setJobs(r.data.jobs || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load jobs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveJob = async (job) => {
    setSaving(true);
    try {
      if (job.id) {
        await api.put(`/admin/sbt-site/careers/${job.id}`, job);
      } else {
        await api.post("/admin/sbt-site/careers", job);
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/sbt-site/careers/${job.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
    finally { setSaving(false); }
  };

  return (
    <section data-testid="admin-careers">
      <Tabs defaultValue="jobs" className="w-full">
        <TabsList>
          <TabsTrigger value="jobs" data-testid="careers-tab-jobs"><Briefcase className="w-3.5 h-3.5 mr-1"/> Open Positions</TabsTrigger>
          <TabsTrigger value="applications" data-testid="careers-tab-apps"><Users className="w-3.5 h-3.5 mr-1"/> Applications</TabsTrigger>
        </TabsList>
        <TabsContent value="jobs" className="mt-4">
          {editing ? (
            <JobEditor job={editing} onCancel={() => setEditing(null)} onSave={saveJob} saving={saving}/>
          ) : (
            <JobsList jobs={jobs} loading={loading} saving={saving}
              onAdd={() => setEditing(emptyJob())}
              onEdit={(j) => setEditing(j)}
              onDelete={deleteJob}
            />
          )}
        </TabsContent>
        <TabsContent value="applications" className="mt-4">
          <Applications />
        </TabsContent>
      </Tabs>
    </section>
  );
}
